import React, { useState, useEffect } from 'react';
import { UserCog, XCircle, Plus, Ban, Trash2, CheckCircle, Loader2, FolderOpen, Users, RefreshCw, Edit, Lock, Save } from 'lucide-react';
import { usersAPI, projectsAPI } from '../services/api';

const UserManagementModal = ({ onClose, onProjectCreated, onProjectDeleted }) => {
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'projects'
  
  // User management state
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('vendor');
  const [editingUser, setEditingUser] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [changingPassword, setChangingPassword] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPasswordChange, setNewPasswordChange] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Project management state
  const [projects, setProjects] = useState([]);
  const [projectsKey, setProjectsKey] = useState(0); // Force re-render key
  const [projectUsers, setProjectUsers] = useState({});
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserRole, setSelectedUserRole] = useState('admin');
  const [expandedProjects, setExpandedProjects] = useState(new Set());

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'projects') {
      // Always refresh projects when switching to projects tab
      // Use preserveExisting=true to avoid overwriting optimistically added projects
      fetchProjects(true);
    }
  }, [activeTab]);
  
  // Also refresh projects when modal opens (if projects tab is active)
  useEffect(() => {
    if (activeTab === 'projects') {
      fetchProjects(true);
    }
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setStatus('خطأ في تحميل المستخدمين');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async (preserveExisting = false) => {
    try {
      setLoadingProjects(true);
      const response = await projectsAPI.getAll();
      const projectsList = response.data || [];
      
      if (preserveExisting) {
        // Merge with existing projects instead of replacing
        setProjects(prev => {
          const merged = [...prev];
          projectsList.forEach(serverProject => {
            const exists = merged.find(p => p.id === serverProject.id);
            if (!exists) {
              merged.push(serverProject);
            } else {
              // Update existing project with server data (merge to preserve any local changes)
              const index = merged.findIndex(p => p.id === serverProject.id);
              merged[index] = { ...merged[index], ...serverProject };
            }
          });
          // Remove any projects that are no longer active (soft-deleted)
          const activeProjects = merged.filter(p => p.is_active !== false);
          return activeProjects.sort((a, b) => a.name.localeCompare(b.name));
        });
      } else {
        // Replace all projects (only when explicitly requested)
        setProjects(projectsList.filter(p => p.is_active !== false));
      }
      
      // Pre-load users for all projects in background (non-blocking)
      projectsList.forEach(project => {
        if (projectUsers[project.id] === undefined) {
          fetchProjectUsers(project.id).catch(err => console.error(`Error pre-loading users for project ${project.id}:`, err));
        }
      });
    } catch (error) {
      console.error('Error fetching projects:', error);
      setStatus('خطأ في تحميل المشاريع');
    } finally {
      setLoadingProjects(false);
    }
  };

  // Load project users (with caching)
  const fetchProjectUsers = async (projectId, forceRefresh = false) => {
    if (!forceRefresh && projectUsers[projectId] !== undefined) {
      return; // Already loaded, but allow force refresh
    }
    try {
      const usersResponse = await projectsAPI.getUsers(projectId);
      setProjectUsers(prev => ({ ...prev, [projectId]: usersResponse.data || [] }));
    } catch (error) {
      console.error(`Error fetching users for project ${projectId}:`, error);
      setProjectUsers(prev => ({ ...prev, [projectId]: [] }));
    }
  };

  const toggleProjectExpansion = (projectId) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
      // Load users when expanding
      fetchProjectUsers(projectId);
    }
    setExpandedProjects(newExpanded);
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProjectName.trim()) {
      setStatus('يرجى إدخال اسم المشروع');
      return;
    }

    try {
      setStatus('جاري إنشاء المشروع...');
      const response = await projectsAPI.create({ name: newProjectName, description: newProjectDescription });
      const projectName = newProjectName; // Save before clearing
      
      // Get the new project from response - handle both response formats
      let newProject;
      if (response.data && response.data.project) {
        newProject = { ...response.data.project };
      } else if (response.data && response.data.id) {
        newProject = {
          id: response.data.id,
          name: projectName,
          description: newProjectDescription || null,
          is_active: true,
          created_by: response.data.created_by || '',
          created_at: new Date().toISOString()
        };
      } else {
        // Fallback: try to extract from response
        newProject = {
          id: response.data?.id || response.data?.project?.id,
          name: projectName,
          description: newProjectDescription || null,
          is_active: true,
          created_by: response.data?.created_by || '',
          created_at: new Date().toISOString()
        };
      }
      
      // Validate that we have a valid project ID
      if (!newProject.id) {
        setStatus('❌ تم إنشاء المشروع لكن لم يتم الحصول على المعرف. يرجى تحديث الصفحة.');
        // Force refresh to get the project from server
        setTimeout(() => fetchProjects(true), 500);
        return;
      }
      
      // Ensure is_active is set
      if (newProject.is_active === undefined) {
        newProject.is_active = true;
      }
      
      // Clear form immediately
      setNewProjectName('');
      setNewProjectDescription('');
      
      // CRITICAL: Immediately add to local state (optimistic update)
      // Ensure all required properties are set
      const projectToAdd = {
        id: newProject.id,
        name: newProject.name || projectName,
        description: newProject.description || null,
        is_active: newProject.is_active !== undefined ? newProject.is_active : true,
        created_by: newProject.created_by || '',
        created_at: newProject.created_at || new Date().toISOString()
      };
      
      // Force React to re-render by updating both state and key
      setProjects(prev => {
        const exists = prev.find(p => p.id === projectToAdd.id);
        if (exists) {
          // Update existing
          const updated = prev.map(p => p.id === projectToAdd.id ? projectToAdd : p);
          return updated.sort((a, b) => a.name.localeCompare(b.name));
        }
        // Add new - create new array reference to force re-render
        const updated = [...prev, projectToAdd];
        return updated.sort((a, b) => a.name.localeCompare(b.name));
      });
      
      // Force re-render by updating key
      setProjectsKey(prev => prev + 1);
      
      // Initialize project users
      setProjectUsers(prev => ({ ...prev, [newProject.id]: [] }));
      
      setStatus(`✅ تم إنشاء المشروع "${projectName}" بنجاح`);
      
      // Notify parent immediately
      if (onProjectCreated) {
        onProjectCreated(newProject);
      }
      
      // Refresh from server after delay (use preserveExisting to merge)
      setTimeout(() => {
        fetchProjects(true).catch(() => {
          // Keep optimistic update even if refresh fails
        });
      }, 500);
      
      setStatus(`✅ تم إنشاء المشروع "${projectName}" بنجاح`);
    } catch (error) {
      console.error('Error creating project:', error);
      setStatus(`❌ فشل في إنشاء المشروع: ${error.response?.data?.error || 'خطأ غير معروف'}`);
    }
  };

  const handleAddUserToProject = async (e) => {
    e.preventDefault();
    // Get project ID from the form's hidden input
    const form = e.target;
    const hiddenInput = form.querySelector('input[type="hidden"]');
    const projectId = hiddenInput ? parseInt(hiddenInput.value) : selectedProject;
    
    if (!projectId || !selectedUserId) {
      setStatus('يرجى اختيار المشروع والمستخدم');
      return;
    }

    try {
      setStatus('جاري إضافة المستخدم...');
      await projectsAPI.addUser(projectId, selectedUserId, selectedUserRole);
      
      const userId = parseInt(selectedUserId);
      const user = users.find(u => u.id === userId);
      
      // Clear selection immediately
      setSelectedUserId('');
      
      // Refresh project users from server to ensure consistency (force refresh)
      await fetchProjectUsers(projectId, true);
      
      // Also update local state optimistically
      if (user) {
        setProjectUsers(prev => {
          const currentUsers = prev[projectId] || [];
          // Check if user already exists
          if (currentUsers.some(u => u.id === userId)) {
            return prev; // Already added
          }
          return {
            ...prev,
            [projectId]: [...currentUsers, {
              id: user.id,
              username: user.username,
              role: user.role,
              project_role: selectedUserRole
            }]
          };
        });
      }
      
      setStatus(`✅ تم إضافة المستخدم "${user?.username || selectedUserId}" إلى المشروع بنجاح`);
    } catch (error) {
      console.error('Error adding user to project:', error);
      setStatus('فشل في إضافة المستخدم إلى المشروع');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    const username = newUsername.trim().toLowerCase();
    
    if (users.some(u => u.username === username)) {
      setStatus('خطأ: اسم المستخدم موجود بالفعل.');
      return;
    }

    if (username.includes(' ') || username.length < 4) {
      setStatus('خطأ: يجب أن يكون اسم المستخدم 4+ أحرف ولا يحتوي على مسافات.');
      return;
    }

    if (!newPassword || newPassword.length < 6) {
      setStatus('خطأ: يجب أن تكون كلمة المرور 6 أحرف على الأقل.');
      return;
    }

    try {
      const response = await usersAPI.create({ username, role: newRole, password: newPassword });
      const newUser = response.data.user;
      
      // Optimistic UI update - add immediately to list
      if (newUser) {
        setUsers(prev => {
          const exists = prev.find(u => u.id === newUser.id);
          if (exists) return prev;
          return [...prev, newUser].sort((a, b) => a.username.localeCompare(b.username));
        });
      }
      
      setStatus(`تم إنشاء المستخدم ${username} (${newRole}) بنجاح.`);
      setNewUsername('');
      setNewPassword('');
      
      // Force immediate refresh to ensure consistency
      // Use setTimeout to allow state update to complete first
      setTimeout(() => {
        fetchUsers().catch(err => console.error('Error refreshing users:', err));
      }, 100);
    } catch (error) {
      console.error("Error creating user:", error);
      setStatus(error.response?.data?.error || 'فشل في إنشاء المستخدم.');
      // Refresh on error to ensure UI is in sync
      fetchUsers();
    }
  };

  const handleEditUser = async (user, newUsername, newRole) => {
    if (user.role === 'superadmin' && user.username === 'superadmin') {
      setStatus('لا يمكن تعديل ملف المدير العام.');
      return;
    }

    try {
      await usersAPI.update(user.id, { username: newUsername, role: newRole });
      setStatus(`تم تحديث بيانات المستخدم بنجاح.`);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error);
      setStatus(error.response?.data?.error || 'فشل في تحديث بيانات المستخدم.');
    }
  };

  const handleDeleteUser = async (user) => {
    if (user.role === 'superadmin' && user.username === 'superadmin') {
      setStatus('لا يمكن حذف المدير العام.');
      return;
    }

    if (!window.confirm(`هل أنت متأكد من حذف المستخدم "${user.username}"؟`)) {
      return;
    }

    try {
      await usersAPI.delete(user.id);
      setStatus(`تم حذف المستخدم ${user.username} بنجاح.`);
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      setStatus(error.response?.data?.error || 'فشل في حذف المستخدم.');
    }
  };

  const handleChangePassword = async (userId) => {
    if (!currentPassword || !newPasswordChange || !confirmPassword) {
      setStatus('يرجى ملء جميع الحقول.');
      return;
    }

    if (newPasswordChange.length < 6) {
      setStatus('كلمة المرور الجديدة يجب أن تكون 6 أحرف على الأقل.');
      return;
    }

    if (newPasswordChange !== confirmPassword) {
      setStatus('كلمات المرور غير متطابقة.');
      return;
    }

    try {
      await usersAPI.changePassword(userId, currentPassword, newPasswordChange);
      setStatus('تم تغيير كلمة المرور بنجاح.');
      setChangingPassword(null);
      setCurrentPassword('');
      setNewPasswordChange('');
      setConfirmPassword('');
    } catch (error) {
      console.error("Error changing password:", error);
      setStatus(error.response?.data?.error || 'فشل في تغيير كلمة المرور.');
    }
  };

  const handleEditProject = async (project, newName, newDescription) => {
    try {
      await projectsAPI.update(project.id, { name: newName, description: newDescription });
      setStatus(`تم تحديث بيانات المشروع بنجاح.`);
      setEditingProject(null);
      fetchProjects(true);
    } catch (error) {
      console.error("Error updating project:", error);
      setStatus(error.response?.data?.error || 'فشل في تحديث بيانات المشروع.');
    }
  };

  const handleDeleteProject = async (project) => {
    if (!window.confirm(`هل أنت متأكد من حذف المشروع "${project.name}"؟ سيتم إلغاء تفعيل المشروع.`)) {
      return;
    }

    try {
      await projectsAPI.delete(project.id);
      setStatus(`تم حذف المشروع ${project.name} بنجاح.`);
      
      // Remove from local state immediately
      setProjects(prev => prev.filter(p => p.id !== project.id));
      setProjectUsers(prev => {
        const updated = { ...prev };
        delete updated[project.id];
        return updated;
      });
      
      // Notify parent to refresh projects in header and dashboard
      if (onProjectDeleted) {
        onProjectDeleted(project.id);
      }
      
      // Refresh from server (non-blocking)
      fetchProjects(true).catch(err => {
        console.error('Error refreshing projects after deletion:', err);
      });
    } catch (error) {
      console.error("Error deleting project:", error);
      setStatus(error.response?.data?.error || 'فشل في حذف المشروع.');
    }
  };

  const handleUpdateUserStatus = async (user, field, value) => {
    if (user.role === 'superadmin' && user.username !== 'superadmin') {
      setStatus('لا يمكن تعديل ملف المدير العام.');
      return;
    }

    try {
      await usersAPI.update(user.id, { field, value });
      setStatus(`تم تحديث حالة المستخدم ${user.username} إلى ${value}.`);
      fetchUsers();
    } catch (error) {
      console.error("Error updating user status:", error);
      setStatus('فشل في تحديث حالة المستخدم.');
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-95 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col p-6">
        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
          <h3 className="text-3xl font-bold text-indigo-700 flex items-center">
            <UserCog className="w-8 h-8 mr-3" /> إدارة النظام
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900 transition p-2">
            <XCircle className="w-8 h-8" />
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex space-x-2 mt-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 font-semibold transition ${
              activeTab === 'users'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            المستخدمين
          </button>
          <button
            onClick={() => setActiveTab('projects')}
            className={`px-4 py-2 font-semibold transition ${
              activeTab === 'projects'
                ? 'border-b-2 border-indigo-600 text-indigo-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <FolderOpen className="w-4 h-4 inline mr-2" />
            المشاريع
          </button>
        </div>
        
        {status && (
          <div className="mt-4 p-3 bg-indigo-100 text-indigo-800 rounded-lg text-sm font-medium">{status}</div>
        )}
        
        {/* Users Tab */}
        {activeTab === 'users' && (
          <>
        {/* New User Creation */}
        <div className="bg-gray-50 p-4 rounded-xl mt-4 mb-6 border border-gray-200">
          <h4 className="text-xl font-semibold text-gray-700 mb-3 flex items-center">
            <Plus className="w-5 h-5 mr-2"/> إنشاء مستخدم نظام جديد (بائع/مدير)
          </h4>
          <form onSubmit={handleCreateUser} className="grid grid-cols-5 gap-3">
            <div className="col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1">اسم المستخدم *</label>
            <input
              type="text"
                placeholder="مثال: vendor104"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
                className="w-full p-3 border rounded-lg"
              required
            />
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-semibold text-gray-700 mb-1">الدور *</label>
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
                className="w-full p-3 border rounded-lg"
            >
              <option value="vendor">بائع</option>
              <option value="admin">مدير</option>
            </select>
            </div>
            <div className="col-span-1">
              <label className="block text-sm font-semibold text-gray-700 mb-1">كلمة المرور *</label>
            <input
              type="password"
                placeholder="6+ أحرف على الأقل"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full p-3 border rounded-lg"
                required
                minLength={6}
              />
            </div>
            <div className="col-span-1 flex items-end">
              <button type="submit" className="w-full bg-green-600 text-white p-3 rounded-lg hover:bg-green-700 transition flex items-center justify-center">
              <Plus className="w-5 h-5 mr-2"/> إنشاء مستخدم
            </button>
            </div>
          </form>
        </div>
        
        {/* User List Table */}
        <div className="flex-grow overflow-y-auto pr-2">
          {loading ? (
            <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-500" /></div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">اسم المستخدم</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">الدور</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">الحالة</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">الإجراءات</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className={user.is_blocked ? 'bg-red-50' : user.is_removed ? 'bg-gray-200' : 'hover:bg-indigo-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                      {user.username}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.role === 'superadmin' ? 'bg-red-200 text-red-800' : user.role === 'admin' ? 'bg-blue-200 text-blue-800' : 'bg-green-200 text-green-800'}`}>
                        {user.role === 'superadmin' ? 'مدير عام' : user.role === 'admin' ? 'مدير' : 'بائع'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.is_blocked && <span className="text-red-600 font-semibold flex items-center"><Ban className="w-4 h-4 mr-1"/> محظور</span>}
                      {user.is_removed && <span className="text-gray-600 font-semibold flex items-center"><Trash2 className="w-4 h-4 mr-1"/> محذوف</span>}
                      {!(user.is_blocked || user.is_removed) && <span className="text-green-600 font-semibold flex items-center"><CheckCircle className="w-4 h-4 mr-1"/> نشط</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      {user.role !== 'superadmin' && !user.is_removed && (
                        <>
                          <button
                            onClick={() => setEditingUser(user)}
                            className="px-3 py-1 text-xs rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition"
                            title="تعديل بيانات المستخدم"
                          >
                            <Edit className="w-3 h-3 inline mr-1"/> تعديل
                          </button>
                          <button
                            onClick={() => setChangingPassword(user.id)}
                            className="px-3 py-1 text-xs rounded-lg bg-purple-500 text-white hover:bg-purple-600 transition"
                            title="تغيير كلمة المرور"
                          >
                            <Lock className="w-3 h-3 inline mr-1"/> كلمة المرور
                          </button>
                        <button
                          onClick={() => handleUpdateUserStatus(user, 'is_blocked', !user.is_blocked)}
                          className={`px-3 py-1 text-xs rounded-lg transition ${user.is_blocked ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}
                          title={user.is_blocked ? 'إلغاء حظر المستخدم' : 'حظر المستخدم'}
                        >
                          {user.is_blocked ? 'إلغاء الحظر' : 'حظر'}
                        </button>
                        <button
                            onClick={() => handleDeleteUser(user)}
                            className="px-3 py-1 text-xs rounded-lg bg-red-600 text-white hover:bg-red-700 transition"
                            title="حذف المستخدم"
                          >
                            <Trash2 className="w-3 h-3 inline mr-1"/> حذف
                        </button>
                        </>
                      )}
                      {user.role === 'superadmin' && <span className="text-gray-400 text-xs">مدير النظام (مقفل)</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        </>
        )}
        
        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <>
            {/* Create New Project - Modern Card Design */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-2xl mt-4 mb-6 border-2 border-indigo-200 shadow-lg">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-2xl font-bold text-indigo-800 flex items-center">
                  <Plus className="w-6 h-6 mr-2 text-indigo-600"/> إنشاء مشروع جديد
              </h4>
                <button
                  onClick={() => fetchProjects(true)}
                  disabled={loadingProjects}
                  className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  title="تحديث قائمة المشاريع"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingProjects ? 'animate-spin' : ''}`} />
                  تحديث
                </button>
              </div>
              <form onSubmit={handleCreateProject} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">اسم المشروع *</label>
                <input
                  type="text"
                    placeholder="أدخل اسم المشروع"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                    className="w-full p-3 border-2 border-indigo-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                  required
                />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">وصف المشروع</label>
                <input
                  type="text"
                    placeholder="وصف اختياري"
                  value={newProjectDescription}
                  onChange={(e) => setNewProjectDescription(e.target.value)}
                    className="w-full p-3 border-2 border-indigo-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                  />
                </div>
                <div className="md:col-span-3">
                  <button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold p-4 rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg flex items-center justify-center transform hover:scale-105"
                  >
                    <Plus className="w-5 h-5 mr-2"/> إنشاء مشروع جديد
                </button>
                </div>
              </form>
            </div>
            
            {/* Projects List - Professional Table View */}
            <div className="flex-grow overflow-y-auto pr-2 mt-4">
              {loadingProjects ? (
                <div className="text-center py-10">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-500" />
                  <p className="mt-2 text-gray-600">جاري تحميل المشاريع...</p>
                </div>
              ) : projects.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                  <FolderOpen className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600 text-lg">لا توجد مشاريع بعد</p>
                  <p className="text-gray-500 text-sm mt-2">قم بإنشاء مشروع جديد أعلاه</p>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                  {/* Table Header */}
                  <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-5">
                    <div className="flex items-center justify-between">
                      <h3 className="text-2xl font-bold text-white flex items-center">
                        <FolderOpen className="w-6 h-6 mr-3" />
                        قائمة المشاريع ({projects.length})
                      </h3>
                      <span className="text-indigo-100 text-base">جميع المشاريع النشطة</span>
                        </div>
                      </div>
                      
                  {/* Projects Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-8 py-5 text-right text-base font-bold text-gray-700 uppercase tracking-wider">المشروع</th>
                          <th className="px-8 py-5 text-right text-base font-bold text-gray-700 uppercase tracking-wider">الوصف</th>
                          <th className="px-8 py-5 text-center text-base font-bold text-gray-700 uppercase tracking-wider">المستخدمون</th>
                          <th className="px-8 py-5 text-center text-base font-bold text-gray-700 uppercase tracking-wider">الإجراءات</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" key={`projects-tbody-${projectsKey}`}>
                        {projects.filter(p => p && p.id && p.is_active !== false).map((project) => {
                          const isExpanded = expandedProjects.has(project.id);
                          const projectUsersList = projectUsers[project.id] || [];
                          return (
                            <React.Fragment key={`project-${project.id}-${projectsKey}`}>
                              <tr className="hover:bg-indigo-50 transition-colors">
                                <td className="px-8 py-5 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <FolderOpen className="w-6 h-6 mr-4 text-indigo-600" />
                                    <div>
                                      <div className="text-base font-bold text-gray-900 mb-1">{project.name}</div>
                                      <div className="text-sm text-gray-500">ID: {project.id}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-8 py-5">
                                  <div className="text-base text-gray-700 max-w-md">
                                    {project.description || <span className="text-gray-400 italic">لا يوجد وصف</span>}
                                  </div>
                                </td>
                                <td className="px-8 py-5 whitespace-nowrap text-center">
                                  <div className="flex items-center justify-center">
                                    <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-green-100 text-green-800">
                                      <Users className="w-4 h-4 mr-2" />
                                      {projectUsersList.length} مستخدم
                                    </span>
                                  </div>
                                </td>
                                <td className="px-8 py-5 whitespace-nowrap text-center">
                                  <div className="flex items-center justify-center gap-2">
                                    <button
                                      onClick={() => setEditingProject(project)}
                                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-md"
                                      title="تعديل المشروع"
                                    >
                                      <Edit className="w-4 h-4 mr-1" />
                                      تعديل
                                    </button>
                                    <button
                                      onClick={() => handleDeleteProject(project)}
                                      className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors shadow-md"
                                      title="حذف المشروع"
                                    >
                                      <Trash2 className="w-4 h-4 mr-1" />
                                      حذف
                                    </button>
                                    <button
                                      onClick={() => toggleProjectExpansion(project.id)}
                                      className="inline-flex items-center px-5 py-3 bg-indigo-600 text-white text-base font-semibold rounded-lg hover:bg-indigo-700 transition-colors shadow-md"
                                    >
                                      <Users className="w-5 h-5 mr-2" />
                                      {isExpanded ? 'إخفاء' : 'عرض'} التفاصيل
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              {/* Expanded Row - User Management */}
                              {isExpanded && (
                                <tr>
                                  <td colSpan="4" className="px-8 py-6 bg-gray-50">
                                    <div className="space-y-6 animate-fadeIn">
                                      {/* Add User to Project Form */}
                                      <div className="bg-white p-6 rounded-lg border-2 border-indigo-200 shadow-sm">
                                        <h5 className="text-lg font-bold text-indigo-800 mb-4 flex items-center">
                                          <Plus className="w-4 h-4 mr-2" /> إضافة مستخدم جديد للمشروع
                                        </h5>
                                        <form onSubmit={handleAddUserToProject} className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                          <input type="hidden" value={project.id} />
                          <select
                            value={selectedUserId}
                            onChange={(e) => {
                                              setSelectedProject(project.id);
                              setSelectedUserId(e.target.value);
                              const user = users.find(u => u.id === parseInt(e.target.value));
                              if (user) {
                                setSelectedUserRole(user.role);
                              }
                            }}
                                            className="p-4 border-2 border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition text-base"
                          >
                            <option value="">اختر المستخدم</option>
                            {users
                              .filter(u => u.role !== 'superadmin' && !u.is_blocked && !u.is_removed)
                              .map(user => (
                                <option key={user.id} value={user.id}>
                                  {user.username} ({user.role === 'admin' ? 'مدير' : 'بائع'})
                                </option>
                              ))}
                          </select>
                          <select
                            value={selectedUserRole}
                            onChange={(e) => setSelectedUserRole(e.target.value)}
                                            className="p-4 border-2 border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition text-base"
                            disabled={!selectedUserId}
                          >
                            <option value="admin">مدير</option>
                            <option value="vendor">بائع</option>
                          </select>
                          <button
                            type="submit"
                                            disabled={!selectedUserId}
                                            className="md:col-span-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold p-4 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-md disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-base"
                          >
                                            <Plus className="w-4 h-4 inline mr-2" /> إضافة مستخدم
                          </button>
                        </form>
                      </div>
                      
                      {/* Project Users List */}
                                      <div className="bg-white p-6 rounded-lg border border-gray-200">
                                        <h5 className="text-lg font-bold text-gray-700 mb-4 flex items-center">
                                          <Users className="w-5 h-5 mr-2 text-indigo-600" /> 
                                          المستخدمون في هذا المشروع ({projectUsersList.length})
                                        </h5>
                                        {projectUsersList.length > 0 ? (
                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {projectUsersList.map((pu) => (
                                              <div key={pu.id} className="flex items-center justify-between bg-gradient-to-r from-gray-50 to-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition">
                                                <div className="flex items-center space-x-3">
                                                  <div className={`w-4 h-4 rounded-full ${
                                                    pu.project_role === 'admin' ? 'bg-blue-500' : 'bg-green-500'
                                                  }`}></div>
                                                  <span className="text-base font-semibold text-gray-800">{pu.username}</span>
                                                </div>
                                                <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                                                  pu.project_role === 'admin' 
                                                    ? 'bg-blue-100 text-blue-800 border border-blue-300' 
                                                    : 'bg-green-100 text-green-800 border border-green-300'
                                                }`}>
                                                  {pu.project_role === 'admin' ? '👤 مدير' : '🏪 بائع'}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                                          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                                            <Users className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                                            <p className="text-base text-gray-600 font-medium">لا يوجد مستخدمون في هذا المشروع</p>
                                            <p className="text-sm text-gray-500 mt-2">استخدم النموذج أعلاه لإضافة مستخدمين</p>
                                          </div>
                        )}
                      </div>
                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      
      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">تعديل بيانات المستخدم</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              handleEditUser(editingUser, formData.get('username'), formData.get('role'));
            }}>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">اسم المستخدم</label>
                <input
                  type="text"
                  name="username"
                  defaultValue={editingUser.username}
                  className="w-full p-3 border rounded-lg"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">الدور</label>
                <select name="role" defaultValue={editingUser.role} className="w-full p-3 border rounded-lg">
                  <option value="vendor">بائع</option>
                  <option value="admin">مدير</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button type="submit" className="flex-1 bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700">
                  <Save className="w-4 h-4 inline mr-2"/> حفظ
                </button>
                <button type="button" onClick={() => setEditingUser(null)} className="flex-1 bg-gray-500 text-white p-3 rounded-lg hover:bg-gray-600">
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {changingPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">تغيير كلمة المرور</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleChangePassword(changingPassword);
            }}>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">كلمة المرور الحالية</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full p-3 border rounded-lg"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">كلمة المرور الجديدة</label>
                <input
                  type="password"
                  value={newPasswordChange}
                  onChange={(e) => setNewPasswordChange(e.target.value)}
                  className="w-full p-3 border rounded-lg"
                  required
                  minLength={6}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">تأكيد كلمة المرور</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full p-3 border rounded-lg"
                  required
                  minLength={6}
                />
              </div>
              <div className="flex gap-2">
                <button type="submit" className="flex-1 bg-purple-600 text-white p-3 rounded-lg hover:bg-purple-700">
                  <Lock className="w-4 h-4 inline mr-2"/> تغيير
                </button>
                <button type="button" onClick={() => {
                  setChangingPassword(null);
                  setCurrentPassword('');
                  setNewPasswordChange('');
                  setConfirmPassword('');
                }} className="flex-1 bg-gray-500 text-white p-3 rounded-lg hover:bg-gray-600">
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      {editingProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">تعديل بيانات المشروع</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              handleEditProject(editingProject, formData.get('name'), formData.get('description'));
            }}>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">اسم المشروع</label>
                <input
                  type="text"
                  name="name"
                  defaultValue={editingProject.name}
                  className="w-full p-3 border rounded-lg"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">وصف المشروع</label>
                <textarea
                  name="description"
                  defaultValue={editingProject.description || ''}
                  className="w-full p-3 border rounded-lg"
                  rows="3"
                />
              </div>
              <div className="flex gap-2">
                <button type="submit" className="flex-1 bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700">
                  <Save className="w-4 h-4 inline mr-2"/> حفظ
                </button>
                <button type="button" onClick={() => setEditingProject(null)} className="flex-1 bg-gray-500 text-white p-3 rounded-lg hover:bg-gray-600">
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementModal;
